import type { FC } from 'react';
import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';

import { SECTION } from 'src/constants/section';
import type { ActionItemSelection } from 'src/types/myFollowUp';
import { FilterProvider } from 'src/components/app/context/FilterContext';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import FilterDisplay from 'src/components/common/FilterDisplay';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import PageFrame from 'src/components/common/PageFrame';
import useInitialFilterGroups from 'src/components/followUp/hooks/useInitialFilterGroups';
import FollowUpActionTable from 'src/components/myFollowUp/FollowUpActionPage/components/FollowUpActionTable/FollowUpActionTable';
import Sidebar from 'src/components/myFollowUp/FollowUpActionPage/components/Sidebar/Sidebar';

import CreateActionItemModalButton from '../MyFollowUpPage/CreateMyFollowUpModal/CreateActionItemModalButton';
import { FollowUpDetailProvider } from '../MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';
import FollowUpDetailModal from '../MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailModal';

import FollowUpActionDownloadButton from './components/FollowUpActionDownloadButton';
import { FollowUpActionTableProvider } from './components/FollowUpActionTable/FollowUpActionTableContext';

const filterSpecs = [
  {
    name: 'dateRange',
    required: true,
    multiple: true,
    hideFilterChips: true,
  },
  {
    name: ORG_UNIT_FILTER_NAME,
    required: true,
    multiple: true,
    hideFilterChips: false,
  },
  {
    name: 'search',
    required: false,
    multiple: false,
    hideFilterChips: true,
  },
  {
    name: 'followUpOwner',
    required: false,
    multiple: true,
    hideFilterChips: false,
  },
  {
    name: 'kpi',
    required: false,
    multiple: true,
    hideFilterChips: false,
  },
  {
    name: 'status',
    required: false,
    multiple: true,
    hideFilterChips: false,
  },
  {
    name: 'hasPriorityOrgUnit',
    required: false,
    multiple: false,
    hideFilterChips: false,
  },
];

const FollowUpActionPage: FC = () => {
  const { currentSurvey } = useUserContext();
  const { moduleId } = useSection();
  const { initialFilterGroups } = useInitialFilterGroups({ dateRange: true });
  const {
    hasWritePermissionForModuleTypeInAnySurvey,
    hasAdminPermissionForModuleTypeInAnySurvey,
  } = useUserPermissions();

  const [selectedRow, setSelectedRow] = useState<ActionItemSelection>({
    surveyId: null,
    actionId: null,
    type: null,
  });

  const createNewActionItem = () => {
    setSelectedRow({
      surveyId: 0,
      actionId: 0,
      type: 'follow-up',
    });
  };

  return (
    <FilterProvider
      key={`${currentSurvey?.id}:${moduleId}`}
      allowCombinedFilters
      initialFilterGroups={initialFilterGroups}
      initialFilterSpecs={filterSpecs}
    >
      <FollowUpActionTableProvider>
        <PageFrame sidebar={<Sidebar />}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
            }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                justifyContent: 'flex-end',
                pb: 2,
                marginInline: 4,
              }}
            >
              <FilterDisplay
                withShowMoreButton
                formatLabel={(name, values) =>
                  `${name}: ${values.map((value) => value.label).join(', ')}`
                }
              />
              <FollowUpDetailProvider
                actionId={selectedRow.actionId}
                enable={selectedRow.type === 'follow-up'}
                onSuccessfulCreation={(newActionItemId) => {
                  setSelectedRow({
                    surveyId: 0,
                    actionId: newActionItemId,
                    type: 'follow-up',
                  });
                }}
              >
                <CreateActionItemModalButton
                  preselectCurrentSurvey
                  onConfirm={createNewActionItem}
                  disabled={
                    !hasWritePermissionForModuleTypeInAnySurvey(
                      SECTION.FOLLOW_UP_ACTION,
                    ) &&
                    !hasAdminPermissionForModuleTypeInAnySurvey(
                      SECTION.FOLLOW_UP_ACTION,
                    )
                  }
                />
                <FollowUpActionDownloadButton />
                <FollowUpDetailModal
                  actionId={selectedRow.actionId}
                  onClose={() =>
                    setSelectedRow({
                      surveyId: null,
                      actionId: null,
                      type: null,
                    })
                  }
                  enable={selectedRow.type === 'follow-up'}
                  onRowSelect={setSelectedRow}
                  useActionContext={true}
                />
              </FollowUpDetailProvider>
            </Stack>
            <FollowUpActionTable
              onRowSelect={(selectedRow: ActionItemSelection) =>
                setSelectedRow(selectedRow)
              }
            />
          </Box>
        </PageFrame>
      </FollowUpActionTableProvider>
    </FilterProvider>
  );
};

export default FollowUpActionPage;
